// Players Directory page with asynchronous filter synchronization
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

const POSITION_OPTIONS = [
  { value: "", label: "All Positions" },
  { value: "Point Guard (PG)", label: "Point Guard (PG)" },
  { value: "Shooting Guard (SG)", label: "Shooting Guard (SG)" },
  { value: "Small Forward (SF)", label: "Small Forward (SF)" },
  { value: "Power Forward (PF)", label: "Power Forward (PF)" },
  { value: "Center (C)", label: "Center (C)" },
];

const GENDER_OPTIONS = [
  { value: "", label: "All Genders" },
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
];

export default function PlayersDirectory() {
  const { user, loading: authLoading } = useAuth();

  // Typed values (which require debouncing to save API calls)
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [minAge, setMinAge] = useState("");
  const [maxAge, setMaxAge] = useState("");
  const [achievement, setAchievement] = useState("");

  // Debounced state mirrors for typed fields
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedLocation, setDebouncedLocation] = useState("");
  const [debouncedMinAge, setDebouncedMinAge] = useState("");
  const [debouncedMaxAge, setDebouncedMaxAge] = useState("");
  const [debouncedAchievement, setDebouncedAchievement] = useState("");

  // Instant dropdown values (No debounce needed!)
  const [position, setPosition] = useState("");
  const [gender, setGender] = useState("");

  // Pagination & Response metadata states
  const [players, setPlayers] = useState([]);
  const [metadata, setMetadata] = useState({ total: 0, page: 1, limit: 20, has_more: false });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Debounce handler tracking typed inputs to insulate database request loops
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(search);
      setDebouncedLocation(location);
      setDebouncedMinAge(minAge);
      setDebouncedMaxAge(maxAge);
      setDebouncedAchievement(achievement);
    }, 350); // 350ms keyboard buffer

    return () => clearTimeout(delayDebounceFn);
  }, [search, location, minAge, maxAge, achievement]);

  // Core API caller
  const fetchPlayers = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      params.append("page", page.toString());
      params.append("limit", "20");

      if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());
      if (debouncedLocation.trim()) params.append("location", debouncedLocation.trim());
      if (position) params.append("position", position);
      if (gender) params.append("gender", gender); // 👈 Correctly mapped backend query parameter
      if (debouncedMinAge) params.append("min_age", debouncedMinAge);
      if (debouncedMaxAge) params.append("max_age", debouncedMaxAge);
      if (debouncedAchievement.trim()) params.append("achievement", debouncedAchievement.trim());

      const { data } = await api.get(`/players/search?${params.toString()}`);
      
      setPlayers(data.players || []);
      setMetadata(data.metadata || { total: 0, page: 1, limit: 20, has_more: false });
    } catch (err) {
      console.error("[directory] query failure", err);
      toast.error("Failed to load player stream records.");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, debouncedLocation, position, gender, debouncedMinAge, debouncedMaxAge, debouncedAchievement, page, user]);

  // Execute fetch when dynamic parameters change
  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  // Auth gate checks
  if (authLoading) return <div className="max-w-7xl mx-auto px-6 py-12 text-zinc-400">Loading directory authentication profile...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const handleFilterChange = (setter, value) => {
    setter(value);
    setPage(1);
  };

  const totalPages = Math.ceil(metadata.total / metadata.limit) || 1;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Upper Headline Profile Tracking section */}
      <div className="mb-8">
        <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Talent Network</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">NO RECRUIT HIDDEN. NO STAT FORGOTTEN.</h1>
        <p className="text-xs font-mono text-zinc-500 mt-1">Find a player, uncover their story. Underrated, overrated, legend or hype—you decide. We just deliver the raw, immutable history.</p>
      </div>

      {/* Main Filter Management Panel */}
      <div className="bg-zinc-950 border border-zinc-800 p-4 mb-8 space-y-4">
        <div className="w-full">
          <input
            type="text"
            placeholder="Search by full name or phone number..."
            value={search}
            onChange={(e) => handleFilterChange(setSearch, e.target.value)}
            className="w-full bg-transparent border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white text-sm"
          />
        </div>

        {/* Filters Row - Always Visible Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t border-zinc-900 items-start">
          
          {/* Location Filter */}
          <label className="block">
            <span className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Location</span>
            <input
              type="text"
              placeholder="City/Region..."
              value={location}
              onChange={(e) => handleFilterChange(setLocation, e.target.value)}
              className="w-full mt-1.5 bg-transparent border border-zinc-800 focus:border-court py-2 px-3 text-xs outline-none text-white h-[34px]"
            />
          </label>

          {/* Position Select Dropdown Menu */}
          <label className="block">
            <span className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Court Position</span>
            <select
              value={position}
              onChange={(e) => handleFilterChange(setPosition, e.target.value)}
              className="w-full mt-1.5 bg-zinc-950 border border-zinc-800 focus:border-court py-2 px-3 text-xs outline-none text-white cursor-pointer h-[34px]"
            >
              {POSITION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-zinc-950">{opt.label}</option>
              ))}
            </select>
          </label>

          {/* Gender Select Dropdown Menu */}
          <label className="block">
            <span className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Gender</span>
            <select
              value={gender}
              onChange={(e) => handleFilterChange(setGender, e.target.value)}
              className="w-full mt-1.5 bg-zinc-950 border border-zinc-800 focus:border-court py-2 px-3 text-xs outline-none text-white cursor-pointer h-[34px]"
            >
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-zinc-950">{opt.label}</option>
              ))}
            </select>
          </label>

          {/* Combined Age Fields bounds */}
          <label className="block">
            <span className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Age Scope Boundaries</span>
            <div className="flex gap-2 mt-1.5">
              <input
                type="number"
                placeholder="Min"
                value={minAge}
                onChange={(e) => handleFilterChange(setMinAge, e.target.value)}
                className="w-1/2 bg-transparent border border-zinc-800 focus:border-court py-2 px-3 text-xs outline-none text-white h-[34px]"
              />
              <input
                type="number"
                placeholder="Max"
                value={maxAge}
                onChange={(e) => handleFilterChange(setMaxAge, e.target.value)}
                className="w-1/2 bg-transparent border border-zinc-800 focus:border-court py-2 px-3 text-xs outline-none text-white h-[34px]"
              />
            </div>
          </label>

          {/* Achievements Substring Matcher */}
          <label className="block">
            <span className="text-[10px] font-bold tracking-widest uppercase text-zinc-500">Specific Accolade</span>
            <input
              type="text"
              placeholder="e.g., National Team / State"
              value={achievement}
              onChange={(e) => handleFilterChange(setAchievement, e.target.value)}
              className="w-full mt-1.5 bg-transparent border border-zinc-800 focus:border-court py-2 px-3 text-xs outline-none text-white h-[34px]"
            />
          </label>
        </div>
      </div>

      {/* Renders Core Grid Framework Data Stream block */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 opacity-40 pointer-events-none">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-44 bg-zinc-900/50 border border-zinc-800 animate-pulse rounded" />
          ))}
        </div>
      ) : players.length === 0 ? (
        <div className="border border-dashed border-zinc-800 p-12 text-center text-sm text-zinc-500">
          No available active player matches found inside database indices.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {players.map((p) => (
              <div 
                key={p.id} 
                className="bg-transparent border border-zinc-800 p-5 flex flex-col justify-between hover:border-zinc-700 transition-all group relative"
              >
                <div>
                  <div className="flex items-start justify-between">
                    <h3 className="font-display font-black text-xl tracking-tight uppercase group-hover:text-court transition-colors">
                      {p.name || "Anonymous Player"}
                    </h3>
                    {p.age && <span className="text-xs font-mono text-zinc-500">{p.age}y</span>}
                  </div>

                  {/* Added visual representation of Gender on the card details line 👇 */}
                  <div className="text-[10px] font-mono text-zinc-400 mt-1 uppercase tracking-wider">
                    {p.position || "Undecided"}{p.gender ? ` · ${p.gender}` : ""}{p.height_cm ? ` · ${p.height_cm}cm` : ""}
                  </div>

                  {p.location && (
                    <div className="text-xs text-zinc-500 mt-2 flex items-center gap-1.5">
                      <span className="text-zinc-600">📍</span> {p.location}
                    </div>
                  )}

                  {/* Bubble Tags Collection Loop Block */}
                  {p.achievements && p.achievements.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {p.achievements.slice(0, 2).map((ach, idx) => (
                        <span key={idx} className="bg-zinc-900 border border-zinc-800 text-[10px] px-2 py-0.5 text-zinc-300 rounded">
                          {ach}
                        </span>
                      ))}
                      {p.achievements.length > 2 && (
                        <span className="text-[9px] text-zinc-600 font-mono self-center">
                          +{p.achievements.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer Controls Component */}
                <div className="pt-4 mt-5 border-t border-zinc-900 flex items-center justify-between">
                  {p.instagram ? (
                    <a
                      href={p.instagram.startsWith("http") ? p.instagram : `https://${p.instagram}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] text-zinc-500 hover:text-white transition-colors underline"
                    >
                      Instagram ↗
                    </a>
                  ) : (
                    <span className="text-[11px] text-zinc-700 italic">No Link</span>
                  )}
                  
                  <a
                    href={`/players/${p.id}/portfolio`}
                    className="text-xs font-bold tracking-wider uppercase text-court hover:text-white transition-colors"
                  >
                    View Stats →
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls bar */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-12 pt-4 border-t border-zinc-900 font-mono text-xs">
              <span className="text-zinc-500">
                Page {metadata.page} of {totalPages} ({metadata.total} players discovered)
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  className="px-3 py-1.5 border border-zinc-800 disabled:opacity-20 hover:border-court text-white transition-all disabled:hover:border-zinc-800"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={!metadata.has_more}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 border border-zinc-800 disabled:opacity-20 hover:border-court text-white transition-all disabled:hover:border-zinc-800"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}